import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import './App.css';
import L, { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
delete L.Icon.Default.prototype._getIconUrl;

export default function WhistlerBusTracker() {
  const [busPositions, setBusPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState(null);
  const [tileLayer, setTileLayer] = useState('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'); // Default tile layer

  const tileLayerOptions = [
    { name: 'OpenStreetMap', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
    { name: 'CartoDB Positron', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png' },
    { name: 'CartoDB Dark Matter', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' },
    { name: 'Stamen Terrain', url: 'https://tiles.stadiamaps.com/tiles/stamen_terrain/{z}/{x}/{y}.jpg' },
    { name: 'Stamen Toner', url: 'https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}.png' },
  ];

  const fetchBusData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://wandering-mud-98e7.krumm.workers.dev/?type=whistlerbuspositions');
      const data = await response.json();
      setBusPositions(data.positions);
      setLastUpdated(data.last_updated || new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      console.error('Error fetching bus data:', err);
      setError('Failed to load bus data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusData();
    const intervalId = setInterval(fetchBusData, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const createBusIcon = (routeNumber, direction) => {
    const routeColor = `#${((parseInt(routeNumber, 10) * 1234567) % 0xffffff).toString(16).padStart(6, '0')}`;
    const arrowColor = routeColor;

    return new Icon({
      iconUrl: `data:image/svg+xml;charset=utf-8,` +
        encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60" width="60" height="60">
            <circle cx="30" cy="30" r="20" fill="${routeColor}" stroke="white" stroke-width="2"/>
            <text x="30" y="30" text-anchor="middle" dy=".3em" font-size="14" fill="white" font-family="Arial" font-weight="bold">${routeNumber}</text>
            <path d="M30 0 L35 10 L25 10 Z" transform="rotate(${direction}, 30, 30)" fill="${arrowColor}"/>
          </svg>
        `),
      iconSize: [50, 50],
      iconAnchor: [25, 25],
      popupAnchor: [0, -25],
    });
  };

  return (
    <div className="flex">
      <header>
        <div className="container">
          <h1>Whistler Bus Tracker</h1>
          <div className="flex">
            <p>Last updated: {lastUpdated}</p>
            <button onClick={fetchBusData} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh Now'}
            </button>
          </div>
          <div>
            <label htmlFor="tileLayerSelect">Map Style: </label>
            <select
              id="tileLayerSelect"
              value={tileLayer}
              onChange={(e) => setTileLayer(e.target.value)}
            >
              {tileLayerOptions.map((option) => (
                <option key={option.name} value={option.url}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main>
        {error && (
          <div className="error">
            <p>{error}</p>
          </div>
        )}

        <MapContainer center={[50.1162, -122.9570]} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url={tileLayer} />
          {busPositions.map((bus) => (
            <Marker
              key={bus.bus_number}
              position={[bus.lat, bus.lon]}
              icon={createBusIcon(bus.route_number, bus.bearing || 0)}
            >
              <Popup className="bus-popup">
                <div>
                  <div className="font-bold" style={{ color: `#${bus.colour}` }}>
                    Route {bus.route_number}: {bus.headsign}
                  </div>
                  <div>
                    <span>Bus:</span> #{bus.vehicle_name}
                  </div>
                  <div>
                    <span>Status:</span> {bus.adherence ? bus.adherence.description : 'No schedule data'}
                  </div>
                  <div>
                    <span>Occupancy:</span> {bus.occupancy_name || 'Unknown'}
                  </div>
                  {bus.speed > 0 && (
                    <div>
                      <span>Speed:</span> {bus.speed} km/h
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </main>

      <footer>
        <p>Data provided by BC Transit. Updates every minute.</p>
      </footer>
    </div>
  );
}
